export const PRODUCT_SEARCH_DESCRIPTION_PREVIEW_LENGTH = 20;

export const truncateProductSearchPreviewText = (
  value = "",
  maxLength = PRODUCT_SEARCH_DESCRIPTION_PREVIEW_LENGTH
) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1)).trimEnd()}...`;
};

export const getProductSearchColumnLabel = (colKey) => {
  const key = String(colKey || "").trim();
  if (key === "thumbnail") return "Thumbnail";
  if (key === "lot") return "Lot #";
  if (key === "title") return "Title";
  if (key === "auctionId") return "Auction";
  if (key === "auctionEndDate") return "Auction End Date";
  if (key === "bidderName") return "Bidder Name";
  if (key === "emailLower") return "Email";
  if (key === "userType") return "User Type";
  if (key === "bid") return "High Bid";
  if (key === "bids") return "Bids";
  if (key === "watches") return "Watches";
  if (key === "views") return "Views";
  if (key === "description") return "Details";
  if (key === "quantity") return "Quantity";
  if (key === "consignor") return "Consigner";
  if (key === "startBid") return "Start Bid";
  if (key === "location") return "Location";
  if (key === "retailPrice") return "Retail";
  if (key === "sellingToRetail") return "Selling $ to Retail";
  if (key === "upcAuctionRuns") return "# of Auctions Run";
  if (key === "upc") return "UPC";
  if (key === "condition") return "Condition";
  if (key === "manufacturer") return "Manufacturer";
  if (key === "color") return "Color";
  if (key === "weight") return "Weight";
  if (key === "dimensions") return "Dimensions";
  if (key === "brand") return "Brand";
  if (key === "lister") return "Lister";
  if (key === "mediaUrls") return "Media";
  return key || "Field";
};

export const formatProductSearchAuctionId = (rawValue) => {
  const raw = String(rawValue || "").trim();
  if (!raw) return "—";
  const base = raw.split("__")[0] || raw;
  const pretty = String(base || "").replace(/-/g, " ").trim();
  return pretty || "—";
};

export const formatProductSearchAuctionEndDate = (rawValue) => {
  const ms = Number(rawValue || 0) || 0;
  if (ms <= 0) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(ms));
  } catch (_) {
    return "—";
  }
};

export const getProductSearchRawCellValue = (
  row,
  colKey,
  {
    getProductSearchThumbnailUrl,
    getLotDescriptionText,
    formatLotDimensions,
    getListernautMediaUrls,
  }
) => {
  const key = String(colKey || "").trim();
  if (!key) return "";
  if (key === "thumbnail") return getProductSearchThumbnailUrl(row);
  if (key === "auctionEndDate") return row?.auctionEndAtMs ?? row?.auctionEndDate;
  if (key === "title") return String(row?.listernautTitle || row?.title || "").trim();
  if (key === "description") return getLotDescriptionText(row);
  if (key === "quantity") return row?.quantity ?? row?.listernautQuantity;
  if (key === "consignor") return row?.consignor ?? row?.listernautConsignorName ?? row?.sellerCode ?? row?.listernautSellerCode;
  if (key === "userType") return row?.userType ?? row?.customerType ?? row?.derived?.customerType;
  if (key === "startBid") return row?.startBid ?? row?.listernautStartBid;
  if (key === "location") return row?.location ?? row?.listernautLocation;
  if (key === "retailPrice") return row?.retailPrice ?? row?.listernautRetailPrice;
  if (key === "sellingToRetail") return row?.sellingToRetail;
  if (key === "upcAuctionRuns") return row?.upcAuctionRuns;
  if (key === "upc") return row?.upc ?? row?.listernautUpc;
  if (key === "condition") return row?.condition ?? row?.listernautCondition;
  if (key === "manufacturer") return row?.manufacturer ?? row?.listernautManufacturer;
  if (key === "color") return row?.color ?? row?.listernautColor;
  if (key === "weight") return row?.weight ?? row?.listernautWeight;
  if (key === "dimensions") return String(row?.dimensions || "").trim() || formatLotDimensions(row);
  if (key === "brand") return row?.brand ?? row?.listernautBrand;
  if (key === "lister") return row?.lister ?? row?.listernautLister;
  if (key === "mediaUrls") {
    if (Array.isArray(row?.mediaUrls)) {
      return row.mediaUrls
        .map((item) => String(item || "").trim())
        .filter(Boolean)
        .join("\n");
    }
    const flatMediaUrls = String(row?.mediaUrls || "").trim();
    return flatMediaUrls || getListernautMediaUrls(row).join("\n");
  }
  if (key === "bids") return row?.bids;
  return row?.[key];
};

export const getProductSearchCellValue = (
  row,
  colKey,
  {
    getProductSearchThumbnailUrl,
    getLotDescriptionText,
    formatLotDimensions,
    getListernautMediaUrls,
    safeNum,
    moneyPlain,
    formatNumber2,
    toAbsInt,
    forExport = false,
  }
) => {
  const key = String(colKey || "").trim();
  if (!key) return "—";
  const value = getProductSearchRawCellValue(row, key, {
    getProductSearchThumbnailUrl,
    getLotDescriptionText,
    formatLotDimensions,
    getListernautMediaUrls,
  });
  if (value === null || value === undefined || value === "") return "—";
  if (key === "description" && !forExport) {
    return truncateProductSearchPreviewText(value);
  }
  if (key === "auctionId") return formatProductSearchAuctionId(value);
  if (key === "auctionEndDate") return formatProductSearchAuctionEndDate(value);
  if (key === "startBid" || key === "retailPrice" || key === "bid") {
    return safeNum(value || 0) > 0 ? `$${moneyPlain(value || 0)}` : "—";
  }
  if (key === "sellingToRetail") {
    return safeNum(value || 0) > 0 ? `${formatNumber2(safeNum(value || 0) * 100)}%` : "—";
  }
  if (key === "bids" || key === "watches" || key === "views") return formatNumber2(toAbsInt(value || 0));
  if (key === "quantity") return toAbsInt(value || 0) > 0 ? formatNumber2(toAbsInt(value || 0)) : "—";
  if (key === "weight") return safeNum(value || 0) > 0 ? formatNumber2(value || 0) : "—";
  if (typeof value === "number") return formatNumber2(value);
  return String(value || "").trim() || "—";
};
